import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, ShieldCheck, Sparkles } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { api, ApiError } from '@/lib/api';
import { useAuth, type Usuario, type EnteOpcion } from '@/lib/authStore';

type LoginResp = {
  token: string;
  expira_en: string;
  usuario: Usuario;
  ente_actual: EnteOpcion | null;
  entes_opciones: EnteOpcion[];
};

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const r = await api<LoginResp>('/auth/login', {
        method: 'POST',
        body: { email, password },
        skipAuth: true,
      });
      setSession(r);
      navigate('/app');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo ingresar');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid h-full w-full lg:grid-cols-[1fr_1.1fr]">
      <div className="flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm">
          <Logo className="mb-10" />
          <h1 className="font-display text-[32px] font-bold leading-tight tracking-tight text-ink">
            Bienvenido de vuelta
          </h1>
          <p className="mt-2 text-[14px] text-ink-mute">
            Accede a la plataforma de gestión gubernamental.
          </p>

          <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4">
            <Input
              name="email"
              type="email"
              label="Correo institucional"
              placeholder="nombre@municipio.gob.mx"
              autoComplete="email"
              leading={<Mail size={16} />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              name="password"
              type="password"
              label="Contraseña"
              autoComplete="current-password"
              leading={<Lock size={16} />}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              error={error ?? undefined}
            />
            <div className="flex items-center justify-between text-[13px]">
              <label className="inline-flex items-center gap-2 text-ink-mute">
                <input type="checkbox" className="h-3.5 w-3.5 rounded border-border accent-brand-500" />
                Recordarme
              </label>
              <a href="#" className="font-medium text-brand-600 hover:underline">¿Olvidaste tu contraseña?</a>
            </div>
            <Button type="submit" size="lg" loading={loading} className="mt-2">
              Ingresar
            </Button>
          </form>

          <p className="mt-8 text-[12px] text-ink-soft">
            Al continuar aceptas los términos de uso y la política de privacidad conforme a LGPDPPSO.
          </p>
        </div>
      </div>

      <div className="relative hidden overflow-hidden bg-gradient-to-br from-brand-700 via-brand-600 to-brand-500 lg:block">
        <div className="absolute inset-0 opacity-[0.14]" style={{backgroundImage:'radial-gradient(circle at 20% 30%, white, transparent 50%), radial-gradient(circle at 80% 70%, white, transparent 45%)'}} />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22><path d=%22M0 39h40M39 0v40%22 stroke=%22rgba(255,255,255,0.06)%22 fill=%22none%22/></svg>')]" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.18em] text-white/70">
            <Sparkles size={14} />
            Nueva generación
          </div>

          <div>
            <h2 className="font-display text-[44px] font-bold leading-[1.05] tracking-tight">
              Gobernar con<br />
              <span className="italic text-brand-100">claridad</span> y evidencia.
            </h2>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-white/85">
              Finanzas, materiales y recursos humanos en una sola plataforma,
              alineada a LGCG, CONAC y disciplina financiera. Auditable de punta a punta.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-5 max-w-md">
              <FeatureItem icon={<ShieldCheck size={16} />} label="Bitácora inviolable" detail="Cadena de hashes SHA-256 + Ed25519" />
              <FeatureItem icon={<Sparkles size={16} />} label="Armonización CONAC" detail="Catálogos y matriz Art. 41 LGCG" />
            </div>
          </div>

          <div className="flex items-center justify-between text-[12px] text-white/60">
            <span>© 2026 SCGMEX</span>
            <span>v0.1.0 · F0</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureItem({ icon, label, detail }: { icon: React.ReactNode; label: string; detail: string }) {
  return (
    <div className="rounded-lg border border-white/15 bg-white/5 p-3.5 backdrop-blur-sm">
      <div className="flex items-center gap-2 text-white/90">
        {icon}
        <span className="text-[13px] font-semibold">{label}</span>
      </div>
      <p className="mt-1 text-[12px] text-white/60">{detail}</p>
    </div>
  );
}
